'use client'

import { createContext, useContext, useState, useEffect } from 'react'
import axios from '@/utils/assessmentAxiosInterceptor'
import { sessionManager } from '@/utils/sessionManager'

const AuthContext = createContext(null)

export const AUTH_TYPES = {
    LOGIN: 'login',
    REGISTER: 'register',
    PASSWORD_RESET: 'password-reset',
    RECAPTCHA_VERIFY: 'recaptcha-verify',
    MFA_VERIFY: 'mfa-verify',
    MFA_SELECT_TYPE: 'mfa-select-type',
    OAUTH_CONSENT: 'oauth-consent',
}

const getUrlParams = () => {
    if (typeof window === 'undefined') return {}
    const params = new URLSearchParams(window.location.search)
    return {
        email: params.get('email'),
        firstName: params.get('firstName'),
        lastName: params.get('lastName'),
        // OAuth parameters
        client_id: params.get('client_id'),
        redirect_uri: params.get('redirect_uri'),
        scope: params.get('scope'),
        state: params.get('state'),
        response_type: params.get('response_type'),
        code_challenge: params.get('code_challenge'),
        code_challenge_method: params.get('code_challenge_method'),
        source: params.get('source'),
    }
}

const redirectToHome = (redirect, oauthParams, setSelectedType) => {
    // If this is an OAuth flow, redirect to consent screen instead
    if (oauthParams) {
        setSelectedType(AUTH_TYPES.OAUTH_CONSENT)
        return
    }
    const params = new URLSearchParams(window.location.search)
    window.location = params.get('redirect') || redirect || '/'
}

const handleApiError = (error, setError) => {
    console.error(error)

    if (error?.response?.data?.code) {
        switch (error.response.data.code) {
            case 'VALIDATION_ERROR':
                return setError(error.response.data.details.errors.map((e) => e.message))
            case 'EMAIL_EXISTS':
                return setError(['An account with this email already exists. Please log in.'])
            case 'INVALID_CODE':
                return setError(['The code you entered is invalid. Please check and try again.'])
            case 'CODE_EXPIRED':
                return setError(['The code you entered has expired. Please request a new one.'])
            case 'EXTERNAL_PROVIDER':
                const provider = error.response.data.details.provider || 'an external provider'
                return setError([`This email is registered with ${provider}. Please log in using that provider.`])
            case 'RATE_LIMIT_EXCEEDED':
                return setError(['Too many attempts. Please wait a moment and try again.'])
            case 'UNAUTHORIZED':
                return setError(['The email or password you entered is incorrect. Please try again.'])

            default:
                return setError(['An unexpected error occurred. Please try again.'])
        }
    }

    if (error?.response?.data?.message) {
        return setError([error.response.data.message])
    }

    setError(['An unexpected error occurred. Please try again.'])
}

const executeRecaptcha = (action) => {
    return new Promise((resolve, reject) => {
        if (typeof window.grecaptcha === 'undefined') {
            reject(new Error('reCAPTCHA is not loaded'))
            return
        }

        grecaptcha.ready(() => {
            grecaptcha.execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, { action }).then(resolve).catch(reject)
        })
    })
}

export function AuthProvider({ children, initialType, isOAuthPage = false, clientData = null, skipToConsent = false }) {
    const urlParams = getUrlParams()
    const isOAuthFlow = isOAuthPage && !!(urlParams.client_id && urlParams.redirect_uri)
    const [selectedType, setSelectedType] = useState(
        skipToConsent ? AUTH_TYPES.OAUTH_CONSENT : urlParams.email ? AUTH_TYPES.REGISTER : initialType
    )
    const [showVerification, setShowVerification] = useState(false)
    const [error, setError] = useState([])
    const [email, setEmail] = useState(null)
    const [password, setPassword] = useState(null)
    const [externalAuthLoading, setExternalAuthLoading] = useState(false)
    const [mfaSession, setMfaSession] = useState(null)
    const [oauthParams] = useState(
        isOAuthFlow
            ? {
                  client_id: urlParams.client_id,
                  redirect_uri: urlParams.redirect_uri,
                  scope: urlParams.scope,
                  state: urlParams.state,
                  response_type: urlParams.response_type || 'code',
                  code_challenge: urlParams.code_challenge || undefined,
                  code_challenge_method: urlParams.code_challenge_method || undefined,
              }
            : null
    )
    const [oauthClientData] = useState(clientData)

    useEffect(() => {
        setError([])
        setShowVerification(false)
    }, [selectedType])

    const login = async (loginEmail, loginPassword) => {
        setError([])
        try {
            const token = await executeRecaptcha('submit')
            const { data } = await axios.post('/assessment/api/auth/login', {
                email: loginEmail,
                password: loginPassword,
                captchaToken: token,
            })

            if (data.verification || data.recaptchaFailure) {
                setEmail(loginEmail)
                setPassword(loginPassword)

                if (data.recaptchaFailure) {
                    setSelectedType(AUTH_TYPES.RECAPTCHA_VERIFY)
                }
                return
            }

            redirectToHome(null, oauthParams, setSelectedType)
        } catch (error) {
            if (error?.response?.status === 401 && error?.response?.data?.code === 'MFA_REQUIRED') {
                setEmail(loginEmail)
                setPassword(loginPassword)

                // Check if we need to select MFA type first
                if (error.response.data.challengeName === 'SELECT_MFA_TYPE') {
                    setMfaSession({
                        challengeName: error.response.data.challengeName,
                        session: error.response.data.session,
                        mfaOptions: error.response.data.mfaOptions,
                    })
                    setSelectedType(AUTH_TYPES.MFA_SELECT_TYPE)
                    return
                }

                // Standard MFA challenge
                setMfaSession({
                    challengeName: error.response.data.challengeName,
                    session: error.response.data.session,
                    challengeParameters: error.response.data.challengeParameters,
                })
                setSelectedType(AUTH_TYPES.MFA_VERIFY)
                return
            }

            if (error?.response?.status === 400 && error?.response?.data?.code === 'USER_NOT_CONFIRMED') {
                setEmail(loginEmail)
                setPassword(loginPassword)
                setShowVerification(true)
                await resendConfirmation(loginEmail)
                return
            }

            handleApiError(error, setError)
        }
    }

    const register = async (registerEmail, registerPassword, firstName, lastName, source) => {
        setError([])
        try {
            const token = await executeRecaptcha('submit')
            await axios.post('/assessment/api/auth/register', {
                email: registerEmail,
                password: registerPassword,
                first_name: firstName,
                last_name: lastName,
                captchaToken: token,
                source,
            })

            setEmail(registerEmail)
            setPassword(registerPassword)
            setShowVerification(true)
        } catch (error) {
            if (error?.response?.data?.redirectToSignIn) {
                setError([error.response.data.message])

                try {
                    const token = await executeRecaptcha('submit')
                    await axios.post('/assessment/api/auth/login', {
                        email: registerEmail,
                        password: registerPassword,
                        captchaToken: token,
                    })

                    redirectToHome(null, oauthParams, setSelectedType)
                    return
                } catch (loginError) {
                    handleApiError(loginError, setError)
                }
                return
            }
            handleApiError(error, setError)
        }
    }

    const sendVerification = async (code) => {
        try {
            const res = await axios.post('/assessment/api/auth/confirmRegister', {
                verificationCode: code,
                email,
                password,
            })

            if (!res.data.success) {
                setError([res.data.message])
                return
            }

            redirectToHome(res?.data?.redirect, oauthParams, setSelectedType)
        } catch (error) {
            handleApiError(error, setError)
        }
    }

    const sendRecaptchaValidation = async (code) => {
        try {
            const res = await axios.post('/assessment/api/auth/captchaCodeVerification', {
                code,
                email,
                password,
            })

            if (!res.data.success) {
                setError([res.data.message])
                return
            }

            redirectToHome(res?.data?.redirect, oauthParams, setSelectedType)
        } catch (error) {
            handleApiError(error, setError)
        }
    }

    const resetPassword = async (code, newPassword) => {
        setError([])
        try {
            const res = await axios.post('/assessment/api/auth/resetPassword', {
                code,
                email,
                password: newPassword,
            })

            if (!res.data.success) {
                setError([res.data.message])
                return
            }

            redirectToHome(null, oauthParams, setSelectedType)
        } catch (error) {
            handleApiError(error, setError)
        }
    }

    const handleVerificationCode = (code, newPassword) => {
        switch (selectedType) {
            case AUTH_TYPES.PASSWORD_RESET:
                return resetPassword(code, newPassword)
            case AUTH_TYPES.RECAPTCHA_VERIFY:
                return sendRecaptchaValidation(code)
            default:
                return sendVerification(code)
        }
    }

    const sendPasswordResetEmail = async (resetEmail) => {
        setError([])
        try {
            const token = await executeRecaptcha('submit')
            const { data } = await axios.post('/assessment/api/auth/forgottenPassword', {
                email: resetEmail,
                captchaToken: token,
            })

            if (data.success) {
                setEmail(resetEmail)
                setShowVerification(true)
            }
        } catch (error) {
            handleApiError(error, setError)
        }
    }

    const resendPasswordReset = async () => {
        try {
            const token = await executeRecaptcha('submit')
            await axios.post('/assessment/api/auth/forgottenPassword', {
                email,
                captchaToken: token,
            })
        } catch (error) {
            handleApiError(error, setError)
        }
    }

    const resendConfirmation = async (emailOverride) => {
        const emailToUse = emailOverride || email
        try {
            await axios.post('/assessment/api/auth/resendConfirmation', {
                email: emailToUse,
            })
        } catch (error) {
            handleApiError(error, setError)
        }
    }

    const resendRequest = async (emailOverride) => {
        if (selectedType === AUTH_TYPES.PASSWORD_RESET) {
            await resendPasswordReset()
        } else {
            await resendConfirmation(emailOverride)
        }
    }

    const submitMfaCode = async (code) => {
        setError([])
        try {
            const { data } = await axios.post('/assessment/api/auth/mfa-challenge', {
                email,
                code,
                session: mfaSession.session,
                challengeName: mfaSession.challengeName,
            })

            // Clear MFA session before redirecting
            setMfaSession(null)
            redirectToHome(null, oauthParams, setSelectedType)
        } catch (error) {
            handleApiError(error, setError)
        }
    }

    const selectMfaType = async (mfaType) => {
        setError([])
        try {
            const { data } = await axios.post('/assessment/api/auth/select-mfa-type', {
                email,
                session: mfaSession.session,
                mfaType,
            })

            setMfaSession({
                challengeName: data.challengeName,
                session: data.session,
                challengeParameters: data.challengeParameters,
            })
            setSelectedType(AUTH_TYPES.MFA_VERIFY)
        } catch (error) {
            handleApiError(error, setError)
        }
    }

    const resetLoginProcess = () => {
        setEmail(null)
        setPassword(null)
        setMfaSession(null)
        setError([])
        setSelectedType(AUTH_TYPES.LOGIN)
    }

    const approveOAuthConsent = async () => {
        setError([])
        try {
            const { data } = await axios.post('/assessment/api/auth/external/create-code', oauthParams)

            // Redirect back to the client with authorization code
            const redirectUrl = new URL(oauthParams.redirect_uri)
            redirectUrl.searchParams.set('code', data.code)
            if (oauthParams.state) {
                redirectUrl.searchParams.set('state', oauthParams.state)
            }
            window.location = redirectUrl.toString()
        } catch (error) {
            setError([error?.response?.data?.error_description || 'An unexpected error occurred. Please try again.'])
        }
    }

    const denyOAuthConsent = () => {
        // Redirect back to the client with error
        const redirectUrl = new URL(oauthParams.redirect_uri)
        redirectUrl.searchParams.set('error', 'access_denied')
        redirectUrl.searchParams.set('error_description', 'The user denied the request')
        if (oauthParams.state) {
            redirectUrl.searchParams.set('state', oauthParams.state)
        }
        window.location = redirectUrl.toString()
    }

    const resendMfaCode = async () => {
        setError([])
        if (!email || !password) {
            setError(['Unable to resend code. Please try logging in again.'])
            return
        }

        try {
            sessionManager.clear()
            const token = await executeRecaptcha('submit')
            const { data } = await axios.post('/assessment/api/auth/login', {
                email,
                password,
                captchaToken: token,
            })

            // Login successful without MFA - shouldn't happen but redirect if it does
            if (!data.verification && !data.recaptchaFailure) {
                redirectToHome(null, oauthParams, setSelectedType)
                return
            }
        } catch (error) {
            if (error?.response?.status === 401 && error?.response?.data?.code === 'MFA_REQUIRED') {
                // Check if we need to select MFA type first
                if (error.response.data.challengeName === 'SELECT_MFA_TYPE') {
                    // Automatically select the previously used MFA type if we know it
                    const previousMfaType = mfaSession?.challengeName === 'SMS_MFA' ? 'SMS_MFA' : 'SOFTWARE_TOKEN_MFA'

                    try {
                        const { data } = await axios.post('/assessment/api/auth/select-mfa-type', {
                            email,
                            session: error.response.data.session,
                            mfaType: previousMfaType,
                        })

                        setMfaSession({
                            challengeName: data.challengeName,
                            session: data.session,
                            challengeParameters: data.challengeParameters,
                        })
                        setSelectedType(AUTH_TYPES.MFA_VERIFY)
                        return
                    } catch (selectError) {
                        handleApiError(selectError, setError)
                        return
                    }
                }

                // Standard MFA challenge - just update the session
                setMfaSession({
                    challengeName: error.response.data.challengeName,
                    session: error.response.data.session,
                    challengeParameters: error.response.data.challengeParameters,
                })
                setSelectedType(AUTH_TYPES.MFA_VERIFY)
                return
            }

            handleApiError(error, setError)
        }
    }

    const externalAuthRequested = async () => {
        setError([])
        setExternalAuthLoading(true)

        try {
            const response = await axios.get('/assessment/api/auth/google/initialise')

            const width = 500
            const height = 600
            const left = window.screenX + (window.outerWidth - width) / 2
            const top = window.screenY + (window.outerHeight - height) / 2

            const loginPage = window.open(
                response.data.url,
                null,
                `popup=true,width=${width},height=${height},left=${left},top=${top}`
            )

            // Check if popup closes
            const popupCheckInterval = setInterval(() => {
                if (loginPage?.closed) {
                    clearInterval(popupCheckInterval)
                    setExternalAuthLoading(false)
                    setError(['Login window was closed before completing authentication. Please try again.'])
                }
            }, 1000)

            window.addEventListener(
                'message',
                async (event) => {
                    // Important: Always verify the origin of the message for security
                    if (event.origin !== process.env.NEXT_PUBLIC_API_URL) {
                        return
                    }
                    popupCheckInterval && clearInterval(popupCheckInterval)

                    const { success, code } = event.data

                    loginPage?.close()

                    if (success && code) {
                        try {
                            const exchangeCodeRequest = await axios.post('/assessment/api/auth/exchange-code', {
                                code,
                            })

                            const { isNewUser } = exchangeCodeRequest.data

                            if (isNewUser) {
                                // 5. Use the authorization code to get tokens from your backend
                                try {
                                    const response = await axios.post('/assessment/api/auth/postRegistration', {})
                                    redirectToHome(response.data.redirectUrl, oauthParams, setSelectedType)
                                    return
                                } catch (error) {
                                    setExternalAuthLoading(false)
                                    handleApiError(error, setError)
                                    return
                                }
                            }
                            redirectToHome('/', oauthParams, setSelectedType)
                        } catch (error) {
                            setExternalAuthLoading(false)
                            handleApiError(error, setError)
                        }
                        return
                    }

                    setExternalAuthLoading(false)
                    console.error('Login failed or was cancelled.')
                },
                false
            )
        } catch (error) {
            setExternalAuthLoading(false)
            handleApiError(error, setError)
        }
    }

    const value = {
        selectedType,
        setSelectedType,
        error,
        setError,
        email,
        password,
        setPassword,
        urlParams,
        login,
        register,
        sendPasswordResetEmail,
        handleVerificationCode,
        resendRequest,
        externalAuthRequested,
        externalAuthLoading,
        mfaSession,
        submitMfaCode,
        selectMfaType,
        resetLoginProcess,
        resendMfaCode,
        oauthParams,
        oauthClientData,
        approveOAuthConsent,
        denyOAuthConsent,
        showVerification,
        setShowVerification,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
